(() => {
  'use strict';
  if (customElements.get('ravenox-hero-video')) return;
  class RavenoxHeroVideo extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.video = this.querySelector('video');
      this.toggle = this.querySelector('.ravenox-hero-toggle');
      if (!this.video || !this.toggle) return;
      this.initialized = true;
      this.userPaused = false;
      this.inView = true;
      this.failedSource = '';
      this.pendingPlay = false;
      this.playGeneration = 0;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.connection = navigator.connection;
      this.cleanups = [];
      this.listen = (target, event, callback) => {
        if (!target || !target.addEventListener) return;
        target.addEventListener(event, callback);
        this.cleanups.push(() => target.removeEventListener(event, callback));
      };
      this.listen(this.toggle, 'click', () => {
        this.userPaused = !this.userPaused;
        this.update();
      });
      this.listen(this.video, 'playing', () => {
        this.pendingPlay = false;
        if (!this.mayPlay()) { this.stop(false); return; }
        this.classList.add('is-playing');
        this.toggle.hidden = false;
        this.toggle.textContent = 'Pause video';
      });
      this.listen(this.video, 'error', () => this.fallback());
      this.listen(this.reduced, 'change', () => this.update());
      this.listen(this.mobile, 'change', () => this.update());
      this.listen(this.connection, 'change', () => this.update());
      this.listen(document, 'visibilitychange', () => this.update());
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          this.inView = entries[0].isIntersecting;
          this.update();
        }, { threshold: 0.05 });
        this.observer.observe(this);
      }
      this.update();
    }
    source() {
      return (this.mobile.matches && this.video.dataset.mobileSrc) || this.video.dataset.desktopSrc || '';
    }
    limitedMotion() {
      return this.reduced.matches || Boolean(this.connection &&
        (this.connection.saveData || /(^|-)2g$/.test(this.connection.effectiveType || '')));
    }
    mayPlay() {
      return !this.limitedMotion() && !this.userPaused && this.inView && !document.hidden;
    }
    stop(clearSource) {
      this.playGeneration += 1;
      this.pendingPlay = false;
      this.video.pause();
      this.classList.remove('is-playing');
      if (clearSource && this.video.getAttribute('src')) {
        this.video.removeAttribute('src');
        this.video.load();
      }
    }
    fallback() {
      this.failedSource = this.source();
      this.pendingPlay = false;
      this.stop(true);
      this.toggle.hidden = true;
    }
    update() {
      const source = this.source();
      if (this.limitedMotion()) {
        this.stop(true);
        this.toggle.hidden = true;
        return;
      }
      if (!this.mayPlay()) {
        this.stop(false);
        if (this.userPaused && !this.toggle.hidden) this.toggle.textContent = 'Play video';
        return;
      }
      if (!/^https:\/\//i.test(source) || source === this.failedSource) return;
      if (this.video.getAttribute('src') !== source) {
        this.stop(false);
        this.video.src = source;
        this.video.muted = true;
        this.video.load();
      }
      if (this.pendingPlay || !this.video.paused) return;
      this.pendingPlay = true;
      const generation = ++this.playGeneration;
      let attempt;
      try { attempt = this.video.play(); } catch (_) { this.fallback(); return; }
      if (attempt && attempt.catch) attempt.catch(() => {
        if (generation !== this.playGeneration) return;
        this.pendingPlay = false;
        if (this.mayPlay()) this.fallback();
      });
      else this.pendingPlay = false;
    }
    disconnectedCallback() {
      if (!this.initialized) return;
      this.stop(true);
      if (this.observer) this.observer.disconnect();
      this.cleanups.forEach(cleanup => cleanup());
      this.initialized = false;
    }
  }
  customElements.define('ravenox-hero-video', RavenoxHeroVideo);
})();
